import { useEffect } from "react";

const EDITABLE = "input, textarea, select, [contenteditable='true'], [role='listbox'], [role='option'], [role='menu'], [role='menuitem'], [role='combobox'], [role='slider'], [role='tab'], [role='grid'], [cmdk-root]";

const canScroll = (n) => {
  if (!(n instanceof Element) || n.scrollHeight <= n.clientHeight + 4) return false;
  const oy = getComputedStyle(n).overflowY;
  return oy === "auto" || oy === "scroll";
};

// The page area plus its visible inner scroll boxes (e.g. table bodies), biggest first.
export function scrollers(main) {
  const inner = [...main.querySelectorAll("div")].filter((n) => n.offsetParent !== null && canScroll(n));
  return [main, ...inner.sort((a, b) => b.scrollHeight - a.scrollHeight)];
}

// Scroll by `dy` (or to an edge): the page area first, then the biggest inner box once the page hits its limit.
export function scrollContent(main, dy, edge, preferred) {
  const list = preferred ? [preferred, ...scrollers(main).filter((n) => n !== preferred)] : scrollers(main);
  const room = (n) => (dy ?? (edge === "end" ? 1 : -1)) > 0 ? n.scrollHeight - n.clientHeight - n.scrollTop > 1 : n.scrollTop > 1;
  if (edge) {
    list.forEach((n) => n.scrollTo({ top: edge === "end" ? n.scrollHeight : 0, behavior: "smooth" }));
    return;
  }
  const n = list.find(room);
  if (n) n.scrollBy({ top: dy, behavior: "smooth" });
}

// Keyboard-friendly page area: sets --page-head (sticky page-head height), arrows / PageUp / PageDown / Home / End / Space
// scroll the content (or the box last clicked) when nothing editable is focused, "/" or Ctrl+K focuses the page search.
export function useMainScroll(mainRef) {
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    let head = null;
    let lastBox = null;
    const ro = new ResizeObserver(() => el.style.setProperty("--page-head", `${head?.offsetHeight || 0}px`));
    const bind = () => {
      const h = el.querySelector(".page-sticky");
      if (h === head) return;
      if (head) ro.unobserve(head);
      head = h;
      if (h) ro.observe(h); else el.style.setProperty("--page-head", "0px");
    };
    bind();
    const mo = new MutationObserver(bind);
    mo.observe(el, { childList: true, subtree: true });
    const onDown = (e) => {
      let n = e.target instanceof Element ? e.target : null;
      while (n && n !== el && !canScroll(n)) n = n.parentElement;
      lastBox = n && n !== el ? n : null;
    };
    el.addEventListener("pointerdown", onDown);

    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || document.querySelector("[role='dialog'], [role='alertdialog']")) return;
      const tgt = e.target instanceof Element ? e.target : null;
      const editing = tgt && tgt.closest(EDITABLE);
      if ((e.key === "/" && !editing) || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k")) {
        const inp = [...el.querySelectorAll("input[data-testid*='search'], input[placeholder*='Cari'], input[placeholder*='cari'], input[placeholder*='Search']")]
          .find((x) => x.offsetParent !== null);
        if (inp) { e.preventDefault(); inp.focus(); inp.select?.(); }
        return;
      }
      if (editing || e.ctrlKey || e.metaKey) return;
      if (tgt && tgt !== document.body && !tgt.closest("aside, header, main")) return;
      if (tgt && tgt.closest("button, a") && (e.key === " " || e.key === "Enter")) return;
      const page = el.clientHeight * 0.85;
      const by = { ArrowDown: 64, ArrowUp: -64, PageDown: page, PageUp: -page, " ": e.shiftKey ? -page : page }[e.key];
      const pref = lastBox && el.contains(lastBox) ? lastBox : null;
      if (by !== undefined) { e.preventDefault(); scrollContent(el, by, null, pref); }
      else if (e.key === "Home" || e.key === "End") { e.preventDefault(); scrollContent(el, null, e.key === "End" ? "end" : "start", pref); }
    };
    window.addEventListener("keydown", onKey);
    return () => { ro.disconnect(); mo.disconnect(); el.removeEventListener("pointerdown", onDown); window.removeEventListener("keydown", onKey); };
  }, [mainRef]);
}
